import React, { useState } from 'react';
import { FolderIcon, SearchIcon, ChevronDownIcon, BotIcon, StarIcon, XIcon, PlusIcon, LayersIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface SidebarItem {
  id: string;
  name: string;
  type: 'space' | 'chatbot';
  category?: string;
  isFavorite?: boolean;
  lastAccessed?: string;
}
interface Props {
  items: SidebarItem[];
  activeId?: string;
  onCreateSpace?: () => void;
  onCreateChatbot?: () => void;
  className?: string;
}
export const Sidebar = ({
  items,
  activeId,
  onCreateSpace,
  onCreateChatbot,
  className = ''
}: Props) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['recent', 'favorites']);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  // Group items by category
  const categorizedItems = items.reduce((acc, item) => {
    const category = item.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, SidebarItem[]>);
  // Get recent items
  const recentItems = [...items].sort((a, b) => new Date(b.lastAccessed || '').getTime() - new Date(a.lastAccessed || '').getTime()).slice(0, 5);
  // Get favorite items
  const favoriteItems = items.filter(item => item.isFavorite);
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const filteredItems = items.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
  return <>
      {/* Mobile Trigger */}
      <button onClick={() => setShowMobileSidebar(true)} className="lg:hidden fixed bottom-4 left-4 z-30 p-3 bg-accent hover:bg-accent-light text-white rounded-none shadow-lg transition-colors" aria-label="Open navigation" data-id="element-1386">
        <LayersIcon size={24} data-id="element-1387" />
      </button>
      {/* Sidebar */}
      <div className={`
          fixed lg:relative inset-0 lg:inset-auto z-40 lg:z-0
          transform lg:transform-none transition-transform duration-200 ease-out
          ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${className}
        `} data-id="element-1388">
        <div className="flex flex-col h-full bg-surface-light border-r border-surface-border" data-id="element-1389">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-surface-border" data-id="element-1390">
            <h2 className="text-lg font-medium text-white font-mono" data-id="element-1391">
              Navigation
            </h2>
            <button onClick={() => setShowMobileSidebar(false)} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Close navigation" data-id="element-1392">
              <XIcon size={20} data-id="element-1393" />
            </button>
          </div>
          {/* Search */}
          <div className="p-4" data-id="element-1394">
            <div className="relative group" data-id="element-1395">
              <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 
                         group-focus-within:text-accent transition-colors" data-id="element-1396" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search spaces & chatbots..." className="w-full pl-10 pr-4 py-2 bg-surface border border-surface-border rounded-none
                         text-white placeholder-gray-400 focus:outline-none focus:ring-2
                         focus:ring-accent focus:border-transparent transition-all font-mono" data-id="element-1397" />
            </div>
          </div>
          {/* Quick Actions */}
          <div className="px-4 space-y-2 mb-4" data-id="element-1398">
            <button onClick={onCreateSpace} className="w-full flex items-center gap-2 px-3 py-2 bg-accent 
                       hover:bg-accent-light text-white rounded-none transition-colors
                       font-mono text-sm uppercase tracking-wide" data-id="element-1399">
              <PlusIcon size={16} data-id="element-1400" />
              <span data-id="element-1401">New Space</span>
            </button>
            <button onClick={onCreateChatbot} className="w-full flex items-center gap-2 px-3 py-2 border border-surface-border
                       text-gray-400 hover:text-white hover:bg-surface rounded-none transition-colors
                       font-mono text-sm uppercase tracking-wide" data-id="element-1402">
              <PlusIcon size={16} data-id="element-1403" />
              <span data-id="element-1404">New Chatbot</span>
            </button>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto" data-id="element-1405">
            {searchQuery ?
          // Search Results
          <div className="p-4 space-y-1" data-id="element-1406">
                {filteredItems.map(item => <button key={item.id} onClick={() => {
              navigate(item.type === 'space' ? `/space/${item.id}` : `/chatbot/${item.id}`);
              setShowMobileSidebar(false);
            }} className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-none text-left
                      ${item.id === activeId ? 'bg-accent text-white' : 'text-gray-400 hover:text-white hover:bg-surface'}
                      transition-colors duration-150
                    `} data-id="element-1407">
                    {item.type === 'space' ? <FolderIcon size={18} data-id="element-1408" /> : <BotIcon size={18} data-id="element-1409" />}
                    <span className="truncate font-mono" data-id="element-1410">{item.name}</span>
                  </button>)}
              </div> :
          // Regular Navigation
          <div className="space-y-4" data-id="element-1411">
                {/* Categories */}
                {Object.entries(categorizedItems).map(([category, items]) => <div key={category} data-id="element-1412">
                    <button onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-4 py-2 
                               text-gray-400 hover:text-white transition-colors" data-id="element-1413">
                      <span className="text-sm font-mono uppercase tracking-wide" data-id="element-1414">
                        {category === 'uncategorized' ? 'Other' : category}
                      </span>
                      <ChevronDownIcon size={16} className={`transform transition-transform duration-200
                          ${expandedCategories.includes(category) ? 'rotate-180' : ''}`} data-id="element-1415" />
                    </button>
                    {expandedCategories.includes(category) && <div className="px-4 py-2 space-y-1" data-id="element-1416">
                        {items.map(item => <button key={item.id} onClick={() => {
                  navigate(item.type === 'space' ? `/space/${item.id}` : `/chatbot/${item.id}`);
                  setShowMobileSidebar(false);
                }} className={`
                              w-full flex items-center gap-3 px-3 py-2 rounded-none text-left
                              ${item.id === activeId ? 'bg-accent text-white' : 'text-gray-400 hover:text-white hover:bg-surface'}
                              transition-colors duration-150 font-mono text-sm
                            `} data-id="element-1417">
                            {item.type === 'space' ? <FolderIcon size={18} data-id="element-1418" /> : <BotIcon size={18} data-id="element-1419" />}
                            <span className="truncate" data-id="element-1420">{item.name}</span>
                            {item.isFavorite && <StarIcon size={16} className="ml-auto text-accent-light" data-id="element-1421" />}
                          </button>)}
                      </div>}
                  </div>)}
              </div>}
          </div>
        </div>
      </div>
      {/* Mobile Overlay */}
      {showMobileSidebar && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden" onClick={() => setShowMobileSidebar(false)} data-id="element-1422" />}
    </>;
};